import type { AccountCreditsData } from "@softmax/product-ux/account";
import {
  creditsLevel,
  formatCredits,
  formatNextRefill,
  isAboveCap,
} from "@softmax/web-features/usage";
import type { MyCreditsResponse } from "@softmax/observatory-client/types";

export function buildCreditsView({
  status,
  recent_requests,
}: MyCreditsResponse): AccountCreditsData {
  const aboveCap = isAboveCap(status);
  const level = creditsLevel(status);
  let notice: string | null = null;

  if (!status.enforced)
    notice = "Credit limits are not enforced for this account.";
  else if (level === "over")
    notice = "Experience requests are denied until refills cover the balance.";
  else if (level === "low")
    notice =
      "Credits are running low. Experience requests require an available balance.";

  return {
    balance: formatCredits(status.balance_credits),
    balanceDescription: aboveCap
      ? `Above the ${formatCredits(status.max_balance_credits)}-credit cap`
      : `of ${formatCredits(status.max_balance_credits)} credits`,
    refill: aboveCap
      ? {
          amount: "Paused",
          detail: "Refills pause until spend brings the balance below its cap.",
        }
      : {
          amount: `+${formatCredits(status.refill_credits)}`,
          detail: `every ${status.refill_cadence} · next ${formatNextRefill({ nextRefillAt: status.next_refill_at })}`,
        },
    used: formatCredits(status.spent_this_period_credits),
    conversion: `${formatCredits(status.credits_per_usd)} credits per USD`,
    notice,
    requests: recent_requests.map((row) => ({
      id: row.experience_request_id,
      coworld: row.coworld ?? "—",
      when: when.format(new Date(row.last_activity_at)),
      episodes: row.episodes,
      credits: formatCredits(row.compute_cost_credits),
    })),
  };
}

const when = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
